<template>
  <div v-if="checkingSession" class="session-loading">
    <div class="session-spinner"></div>
  </div>
  <AppLogin v-else-if="!isAuthenticated" />
  <div v-else class="app-wrapper">
    <AppHeader />
    <div class="app-body" :class="{ 'no-sidebar': !showSidebarLayout }">
      <AppLeftSidebar v-if="showSidebarLayout" class="sidebar" @submenu-click="selected = $event" />
      <main class="main-content">
        <router-view />
      </main>
    </div>
  </div>
</template>

<script>
import AppHeader from './components/AppHeader.vue';
import AppLeftSidebar from './components/AppLeftSideBar.vue';
import AppLogin from './components/AppLogin.vue';
import { initPushNotifications, unregisterPushNotifications } from './utils/pushNotifications';

export default {
  name: 'App',
  components: { AppHeader, AppLeftSidebar, AppLogin },
  async mounted() {
    await this.validateSession();
    window.addEventListener('auth-changed', this.validateSession);
  },
  beforeUnmount() {
    window.removeEventListener('auth-changed', this.validateSession);
  },
  provide() {
    return {
      posts: this.posts,
      privilage: () => this.privilage,
    };
  },
  data() {
    return {
      privilage: null,
      isAuthenticated: false,
      checkingSession: true,
      selected: null,
      posts: [],
    };
  },
  computed: {
    showSidebarLayout() {
      return this.privilage === 'admin';
    },
  },
  methods: {
    homeForRole(role) {
      if (role === 'caissier') return '/cashier';
      if (role === 'employe') return '/mes-avances';
      return '/';
    },
    hasSessionCookie() {
      return document.cookie.includes('session_id=');
    },
    clearSessionState() {
      unregisterPushNotifications();
      document.cookie = 'session_id=; Max-Age=0; path=/';
      this.privilage = null;
      this.isAuthenticated = false;
      this.selected = null;
      this.posts = [];
    },
    goToLoginOnce() {
      if (this.$route.path !== '/login') {
        this.$router.replace('/login');
      }
    },
    async validateSession() {
      if (!this.hasSessionCookie()) {
        this.clearSessionState();
        this.checkingSession = false;
        this.goToLoginOnce();
        return;
      }

      try {
        const response = await fetch('/api-pointage/auth-check', {
          credentials: 'include',
          cache: 'no-store',
        });
        if (!response.ok) {
          this.clearSessionState();
          this.checkingSession = false;
          this.goToLoginOnce();
          return;
        }

        const data = await response.json();
        this.privilage = data.role || null;
        this.isAuthenticated = Boolean(data.authenticated && this.privilage);
        if (!this.isAuthenticated) {
          this.clearSessionState();
          this.checkingSession = false;
          this.goToLoginOnce();
          return;
        }

        if (this.privilage === 'admin') {
          await this.fetchPosts();
          await initPushNotifications(this.$router);
        }
        if (this.$route.path === '/login') {
          this.$router.replace(this.homeForRole(this.privilage));
        }
      } catch (error) {
        console.error('Error validating session:', error);
      } finally {
        this.checkingSession = false;
      }
    },
    async fetchPosts() {
      try {
        const response = await fetch('/api-pointage/fetchposts', {
          credentials: 'include',
          cache: 'no-store',
        });
        if (!response.ok) throw new Error('Failed');
        const data = await response.json();
        this.posts = Array.isArray(data) ? data : [];
      } catch (error) {
        console.error('Error fetching posts:', error);
        this.posts = [];
      }
    },
  },
};
</script>

<style>
.session-loading {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--surface);
}

.session-spinner {
  width: 34px;
  height: 34px;
  border: 3px solid var(--border);
  border-top-color: var(--primary);
  border-radius: 50%;
  animation: session-spin 0.8s linear infinite;
}

@keyframes session-spin {
  to {
    transform: rotate(360deg);
  }
}

.app-wrapper {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: var(--surface);
}

.app-body {
  display: flex;
  flex: 1;
  min-height: 0;
}

.main-content {
  flex: 0 1 calc(100% - var(--sidebar-width));
  width: calc(100% - var(--sidebar-width));
  margin-left: var(--sidebar-width);
  padding: 22px 28px;
  min-width: 0;
  min-height: calc(100vh - var(--header-height));
  background:
    linear-gradient(180deg, rgba(255,255,255,0.45), rgba(255,255,255,0) 170px),
    var(--surface);
}

.app-body.no-sidebar .main-content {
  margin-left: 0;
}

@media (max-width: 768px) {
  .main-content {
    flex-basis: 100%;
    width: 100%;
    margin-left: 0;
    padding: 64px 10px 18px;
  }

  .app-body.no-sidebar .main-content {
    padding-top: 18px;
  }
}

@media (max-width: 420px) {
  .main-content {
    padding-left: 8px;
    padding-right: 8px;
  }
}
</style>
